// sarcopeniaReport.js — AI 분석 결과 + 환자 정보로 "Body-inside" PDF 리포트 생성 (zero deps)
// 리포트를 canvas 에 그린 뒤 JPEG 로 인코딩하여 1페이지 A4(세로) PDF 에 삽입한다. (2026-11-04 가로 → 세로)
import Cache from "core/cache/index";
// 한글 이름 복원은 DICOM 파서(core/parser/dicom.js)와 같이 쓰도록 공용 파일로 옮김
import { decodeDicomText } from "core/helper/dicomText";
import BaseBrushTool from "component/molecules/viewport/tools/annotation/base/BaseBrushTool";
import { getEnabledElements } from "component/toolManager/enabledElements.js";
import { getToolState } from "component/toolManager/toolState.js";

/**
 * T-Score = -2.0 기준 cut-off 값 (성별).
 * measurement < cut-off  →  "Sarcopenia", 그 외 "Normal"
 * null 이면 판정하지 않고 "-" / "N/A" 로 표기한다.
 */
export const SARCOPENIA_CUTOFFS = {
  M: { smaHeight2: 39.8, smaBmi: 4.97 },
  F: { smaHeight2: 28.4, smaBmi: 3.46 },
};

// 리포트 영상 표시용 복부 window (W400 / L40)
const WINDOW = { center: 40, width: 400 };

// 마스크 라벨 (view 모드 5-class): 1=SFat 2=VFat 3=IMA 4=LAMA 5=NAMA
// ↓ 2026-11-04 양식(010.pdf)에 맞추기 전 값
// const OVERLAYS = {
//   SMA: [
//     { label: 5, color: [184, 1, 5], alpha: 0.9 }, // NAMA
//     { label: 4, color: [2, 211, 216], alpha: 0.9 }, // LAMA
//   ],
//   VFA: [{ label: 2, color: [67, 38, 126], alpha: 0.7 }],
//   SFA: [{ label: 1, color: [110, 29, 20], alpha: 0.7 }],
// };

// const COLOR = {
//   title: "#2c4e98",
//   blue: "#2f55a0",
//   dark: "#404244",
//   gray: "#ececec",
//   text: "#000000",
// };
// const FONT = 'Arial, "Malgun Gothic", "Apple SD Gothic Neo", sans-serif';

// // 논리 좌표계 (A4 세로 비율) 및 출력 배율
// // const PAGE_W = 1170;   // 이전 A4 가로
// // const PAGE_H = 827;
// const PAGE_W = 827;
// const PAGE_H = 1170;
// const SCALE = 2.5;
// // A4 세로 PDF 크기 (pt). 210 x 297 mm
// const PDF_W = 595.28;
// const PDF_H = 841.89;

// 양식(010.pdf)과 같은 색. 반투명이 아니라 그 색으로 덮는다
const OVERLAYS = {
  SMA: [
    { label: 5, color: [184, 1, 5], alpha: 1 }, // NAMA
    { label: 4, color: [0, 255, 255], alpha: 1 }, // LAMA
  ],
  VFA: [{ label: 2, color: [57, 43, 87], alpha: 1 }],
  SFA: [{ label: 1, color: [70, 29, 25], alpha: 1 }],
};

const COLOR = {
  title: "#2a4d94",
  blue: "#2a4d94", // 이름표·머리글 글자
  dark: "#40464a", // 굵은 선 왼쪽
  bar: "#355798", // 굵은 선 오른쪽, 맨 아래 선
  gray: "#eeeeee",
  text: "#000000",
};
// 양식은 Helvetica. 윈도우에는 Helvetica 가 없어 글자 폭이 같은 Arial 로 그려진다
const FONT = 'Helvetica, Arial, "Malgun Gothic", "Apple SD Gothic Neo", sans-serif';

// 좌표 단위 = PDF pt, 왼쪽 위가 (0,0). A4 세로 595.2756 x 841.8898 (210 x 297 mm)
// 아래 숫자는 양식 PDF 의 위치를 그대로 옮긴 것 (PDF 는 아래가 0 이라 y = 841.8898 - 양식 y)
const PAGE_W = 595.2756;
const PAGE_H = 841.8898;
const SCALE = 3.5; // 2083 x 2947 px, 약 250 dpi
const PDF_W = PAGE_W;
const PDF_H = PAGE_H;

/* ------------------------------------------------------------------ */
/* 데이터 수집                                                          */
/* ------------------------------------------------------------------ */

/** DICOM 태그에서 나이/성별 기본값 추출 (예: "050Y" → 50) */
export function getDicomPatientDefaults() {
  // key: 지금 열려 있는 시리즈. 다른 파일을 열었는지 알아보는 데 쓴다 (reportPanel)
  const result = { age: "", sex: "", key: null };
  try {
    const metadataCache = new Cache.Metadata();
    const key = metadataCache.getActiveDataKey();
    if (!key) return result;
    // result.key = key;
    // 같은 시리즈의 다른 장(파일)을 올려도 새 파일로 보도록 파일 이름까지 붙인다
    let fileName = "";
    try {
      const all = metadataCache.get_all();
      if (all && all.length > 0 && all[0].filename) fileName = all[0].filename;
    } catch (e) {
      // 파일 이름을 모르면 시리즈만으로 구분
    }
    result.key = `${key}|${fileName}`;
    const series = metadataCache._metadataCache.get(key);
    if (!series) return result;

    const ageMatch = /^(\d+)\s*Y?$/i.exec(String(series.patientAge || "").trim());
    if (ageMatch) result.age = String(parseInt(ageMatch[1], 10));

    const sex = String(series.patientSex || "").trim().toUpperCase();
    if (sex === "M" || sex === "F") result.sex = sex;

    // 환자 이름 (DICOM PatientName). "HONG^GILDONG^^" 같은 형식이라 ^ 를 띄어쓰기로 바꾼다
    // const name = String(series.patientName || "").replace(/\^+/g, " ").replace(/\s+/g, " ").trim();
    const name = formatPatientName(series.patientName);
    if (name) result.name = name;
  } catch (e) {
    // DICOM 정보가 없으면 빈 값 사용
  }
  return result;
}

// decodeDicomText → core/helper/dicomText.js 로 옮김
export { decodeDicomText };

/** PatientName 을 보기 좋게: 한글 복원, "영문=한글" 이면 한글 쪽, ^ 는 띄어쓰기 */
function formatPatientName(raw) {
  const decoded = decodeDicomText(raw);
  const groups = decoded.split("=").map((g) => g.trim()).filter(Boolean);
  const picked = groups.find((g) => /[^\u0000-\u007f]/.test(g)) || groups[0] || "";
  return picked.replace(/\^+/g, " ").replace(/\s+/g, " ").trim();
}

/** 현재 화면에 표시 중인 5-class 마스크를 우선 사용하고, 없으면 서버 응답 마스크 사용 */
function resolveMask(store, measure, length) {
  if (store.mode === "view") {
    try {
      const element = getEnabledElements()[0].element;
      const toolState = getToolState(
        element,
        BaseBrushTool.getReferencedToolDataName()
      );
      const data = toolState && toolState.data && toolState.data[0];
      if (data && data.originalPixelData && data.originalPixelData.length === length) {
        return data.originalPixelData;
      }
    } catch (e) {
      // fallthrough
    }
  }
  if (measure && measure.mask && measure.mask.length === length) {
    return Uint8Array.from(measure.mask);
  }
  return null;
}

/** 리포트에 필요한 영상/측정값 수집. 분석 전이면 Error throw */
export function collectReportSource(store) {
  const measure = store._measureDatalist && store._measureDatalist[0];
  if (!measure) {
    throw new Error("Please run the AI analysis first.");
  }

  const metadataCache = new Cache.Metadata();
  const key = metadataCache.getActiveDataKey();
  const frame = key ? metadataCache.getFrame(key, 0) : null;
  if (!frame || !frame.pixelData) {
    throw new Error("No CT image is loaded.");
  }

  const mask = resolveMask(store, measure, frame.width * frame.height);
  if (!mask) {
    throw new Error("Segmentation mask is not available.");
  }

  const area = measure.Area || {};
  return {
    frame,
    mask,
    sliceNo: frame.instanceNumber,
    areas: {
      SMA: Number(area.SMA_Area),
      VFA: Number(area.VFAT_Area),
      SFA: Number(area.SFAT_Area),
    },
  };
}

/** SMI / SMA-BMI 계산 및 판정 */
export function computeIndices({ sma, heightCm, weightKg, sex }) {
  const hM = heightCm / 100;
  const bmi = weightKg / (hM * hM);
  const cut = SARCOPENIA_CUTOFFS[sex] || {};

  const judge = (value, cutoff) => {
    if (!Number.isFinite(value) || cutoff == null) return "N/A";
    return value < cutoff ? "Sarcopenia" : "Normal";
  };

  const smaHeight2 = sma / (hM * hM);
  const smaBmi = sma / bmi;

  return {
    bmi,
    rows: [
      {
        label: "SMA/Height²",
        cutoff: cut.smaHeight2,
        value: smaHeight2,
        result: judge(smaHeight2, cut.smaHeight2),
      },
      {
        label: "SMA/BMI",
        cutoff: cut.smaBmi,
        value: smaBmi,
        result: judge(smaBmi, cut.smaBmi),
      },
    ],
  };
}

/* ------------------------------------------------------------------ */
/* 렌더링                                                               */
/* ------------------------------------------------------------------ */

/** CT(HU windowing) + 지정 라벨 overlay 를 원본 해상도 canvas 로 렌더 */
function renderSlice(frame, mask, overlays) {
  const { width, height, pixelData } = frame;
  const slope = frame.rescaleSlope || 1;
  const intercept = frame.rescaleIntercept || 0;
  const lo = WINDOW.center - WINDOW.width / 2;

  const lut = {};
  overlays.forEach((o) => (lut[o.label] = o));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  const img = ctx.createImageData(width, height);
  const out = img.data;

  for (let i = 0, n = width * height; i < n; i++) {
    const hu = pixelData[i] * slope + intercept;
    let g = ((hu - lo) / WINDOW.width) * 255;
    g = g < 0 ? 0 : g > 255 ? 255 : g;

    let r = g;
    let gr = g;
    let b = g;
    const o = lut[mask[i]];
    if (o) {
      r = g * (1 - o.alpha) + o.color[0] * o.alpha;
      gr = g * (1 - o.alpha) + o.color[1] * o.alpha;
      b = g * (1 - o.alpha) + o.color[2] * o.alpha;
    }
    const p = i * 4;
    out[p] = r;
    out[p + 1] = gr;
    out[p + 2] = b;
    out[p + 3] = 255;
  }
  ctx.putImageData(img, 0, 0);
  return canvas;
}

function fmt(value, digits) {
  return Number.isFinite(value) ? value.toFixed(digits) : "-";
}

// function text(ctx, str, x, y, { size = 18, color = COLOR.blue, align = "left", weight = "normal", maxWidth } = {}) {
// 양식 좌표는 글자 기준선(alphabetic) 위치라서 baseline 기본값을 alphabetic 으로 (이전에는 middle 고정), 기본 크기 10pt
function text(ctx, str, x, y, { size = 10, color = COLOR.blue, align = "left", weight = "normal", maxWidth, baseline = "alphabetic" } = {}) {
  ctx.font = `${weight} ${size}px ${FONT}`;
  ctx.fillStyle = color;
  ctx.textAlign = align;
  // ctx.textBaseline = "middle";
  ctx.textBaseline = baseline;
  let s = String(str);
  if (maxWidth && ctx.measureText(s).width > maxWidth) {
    while (s.length > 1 && ctx.measureText(s + "…").width > maxWidth) {
      s = s.slice(0, -1);
    }
    s += "…";
  }
  ctx.fillText(s, x, y);
}

function drawPatientInfo(ctx, patient) {
  // 양식: 칸 구분선 x. 이름표는 구분선 6pt 왼쪽에 오른쪽 정렬, 값은 6pt 오른쪽에서 시작
  const divs = [309.3071, 422.6929, 536.0787];
  const maxW = [101, 101, 45];
  const rows = [50.3465, 74.3465]; // 글자 기준선 y
  const items = [
    [0, 0, "Name", patient.name || "Anonymized"],
    [0, 1, "Age", `${patient.age} yrs`],
    [0, 2, "Gender", patient.sex],
    [1, 0, "Height", `${patient.heightCm} cm`],
    [1, 1, "Weight", `${patient.weightKg} kg`],
  ];

  ctx.strokeStyle = COLOR.text;
  ctx.lineWidth = 0.5;
  ctx.lineCap = "round";
  items.forEach(([r, c, label, value]) => {
    const div = divs[c];
    const y = rows[r];
    text(ctx, label, div - 6, y, { align: "right" });
    ctx.beginPath();
    ctx.moveTo(div, y + 5);
    ctx.lineTo(div, y - 13);
    ctx.stroke();
    text(ctx, value, div + 6, y, { color: COLOR.text, maxWidth: maxW[c] });
  });
}

function line(ctx, x1, y1, x2, y2) {
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();
}

/** 리포트 전체를 canvas 에 그린다 (A4 세로, 양식 010.pdf 배치) */
export function renderReportCanvas(source, patient) {
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(PAGE_W * SCALE);
  canvas.height = Math.round(PAGE_H * SCALE);
  const ctx = canvas.getContext("2d");
  ctx.scale(SCALE, SCALE);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  ctx.fillStyle = "#ffffff";
  // 595.2756 x 3.5 가 정수가 아니라 마지막 열이 반만 칠해지면 오른쪽 끝에 회색 줄이 생겨서 1 씩 넉넉히 칠한다
  ctx.fillRect(0, 0, PAGE_W + 1, PAGE_H + 1);

  const L = 25.8425; // 왼쪽 여백
  const TW = 510.2362; // 영상·결과 표 폭

  // Title
  text(ctx, "Body-inside", 31.8425, 74.3465, { size: 40, weight: "bold", color: COLOR.title });

  // Patient info (제목 오른쪽 2줄)
  drawPatientInfo(ctx, patient);

  // Divider bar
  ctx.fillStyle = COLOR.dark;
  ctx.fillRect(L, 97.3465, 161.5748, 5.6693);
  ctx.fillStyle = COLOR.bar;
  ctx.fillRect(L + 161.5748, 97.3465, 377.0079, 5.6693);

  // Section title
  ctx.fillStyle = COLOR.gray;
  ctx.fillRect(L, 112.9898, 240.9449, 18);
  const sliceNo = source.sliceNo != null && source.sliceNo !== "" ? source.sliceNo : "-";
  text(ctx, ` Sarcopenia Analysis |   L3 level slice no. ${sliceNo}`, L + 240.9449 / 2, 126.0158, { align: "center" });

  // Image table: 머리줄(회색 18pt) + 영상 170pt 정사각형 3장
  const panels = [
    { key: "SMA", title: "SMA" },
    { key: "VFA", title: "VFA" },
    { key: "SFA", title: "SFA" },
  ];
  const colW = 170.0787;
  const headTop = 140.0158;
  const imgTop = 162.0158;
  const tableBottom = 335.0945;
  ctx.fillStyle = COLOR.gray;
  ctx.fillRect(L, headTop, TW, 18);
  panels.forEach((p, i) => {
    const x = L + i * colW;
    text(ctx, `${p.title} | ${fmt(source.areas[p.key], 4)} cm²`, x + 85.0394, 154.0158, { align: "center" });

    ctx.fillStyle = "#000000";
    ctx.fillRect(x, imgTop, colW, colW);
    const slice = renderSlice(source.frame, source.mask, OVERLAYS[p.key]);
    const s = Math.min(colW / slice.width, colW / slice.height);
    const dw = slice.width * s;
    const dh = slice.height * s;
    ctx.drawImage(slice, x + (colW - dw) / 2, imgTop + (colW - dh) / 2, dw, dh);
  });
  // 양식의 흰 칸선 (영상 사이)
  ctx.strokeStyle = "#ffffff";
  ctx.lineWidth = 1;
  ctx.lineCap = "round";
  line(ctx, L + colW, headTop, L + colW, tableBottom);
  line(ctx, L + colW * 2, headTop, L + colW * 2, tableBottom);

  // Result table: 머리줄 + 2줄, 4칸
  const { rows } = computeIndices({
    sma: source.areas.SMA,
    heightCm: patient.heightCm,
    weightKg: patient.weightKg,
    sex: patient.sex,
  });
  const tTop = 345.0945;
  const cw = 127.5591;
  const colX = [0, 1, 2, 3].map((i) => L + cw * (i + 0.5));
  ctx.fillStyle = COLOR.gray;
  ctx.fillRect(L, tTop, TW, 18);
  text(ctx, "T-Score = -2.0", colX[1], 358.0945, { align: "center" });
  text(ctx, "Measurement", colX[2], 358.0945, { align: "center" });
  text(ctx, "Sarcopenia", colX[3], 358.0945, { align: "center" });

  rows.forEach((row, i) => {
    const y = 376.0945 + i * 18;
    text(ctx, row.label, colX[0], y, { align: "center" });
    text(ctx, row.cutoff == null ? "-" : String(row.cutoff), colX[1], y, { align: "center", color: COLOR.text });
    text(ctx, fmt(row.value, 2 - (row.value >= 10 ? 1 : 0)), colX[2], y, { align: "center", color: COLOR.text });
    text(ctx, row.result, colX[3], y, { align: "center", color: COLOR.text });
  });

  // 맨 아래 선
  ctx.fillStyle = COLOR.bar;
  ctx.fillRect(L, 799.0945, 538.5827, 5.6693);

  return canvas;
}

/* 이전 A4 가로 배치 (2026-11-04 세로로 바꾸며 주석 처리. PAGE_W 1170 x PAGE_H 827 기준 좌표)
function renderReportCanvasLandscape(source, patient) {
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(PAGE_W * SCALE);
  canvas.height = Math.round(PAGE_H * SCALE);
  const ctx = canvas.getContext("2d");
  ctx.scale(SCALE, SCALE);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, PAGE_W, PAGE_H);

  // Title
  text(ctx, "Body-inside", 40, 100, { size: 80, weight: "bold", color: COLOR.title });

  // Patient info
  drawPatientInfo(ctx, patient);

  // Divider bar
  ctx.fillStyle = COLOR.dark;
  ctx.fillRect(29, 176, 322, 11);
  ctx.fillStyle = COLOR.blue;
  ctx.fillRect(351, 176, 754, 11);

  // Section title
  ctx.fillStyle = COLOR.gray;
  ctx.fillRect(29, 208, 481, 37);
  const sliceNo = source.sliceNo != null && source.sliceNo !== "" ? source.sliceNo : "-";
  text(ctx, `Sarcopenia Analysis |   L3 level slice no. ${sliceNo}`, 269.5, 227, { align: "center" });

  // Image panels
  const panels = [
    { key: "SMA", title: "SMA" },
    { key: "VFA", title: "VFA" },
    { key: "SFA", title: "SFA" },
  ];
  const panelW = 338;
  const imgTop = 306;
  const imgH = 341;
  panels.forEach((p, i) => {
    const x = 30 + i * 340;
    ctx.fillStyle = COLOR.gray;
    ctx.fillRect(x - 1, 265, panelW + 1, 34);
    text(ctx, `${p.title} | ${fmt(source.areas[p.key], 4)} cm²`, x + panelW / 2, 282, { align: "center" });

    ctx.fillStyle = "#000000";
    ctx.fillRect(x, imgTop, panelW, imgH);
    const slice = renderSlice(source.frame, source.mask, OVERLAYS[p.key]);
    const s = Math.min(panelW / slice.width, imgH / slice.height);
    const dw = slice.width * s;
    const dh = slice.height * s;
    ctx.drawImage(slice, x + (panelW - dw) / 2, imgTop + (imgH - dh) / 2, dw, dh);
  });

  // Result table
  const { rows } = computeIndices({
    sma: source.areas.SMA,
    heightCm: patient.heightCm,
    weightKg: patient.weightKg,
    sex: patient.sex,
  });
  const colX = [155, 411, 666, 921];
  ctx.fillStyle = COLOR.gray;
  ctx.fillRect(29, 673, 1019, 35);
  text(ctx, "T-Score = -2.0", colX[1], 690, { align: "center" });
  text(ctx, "Measurement", colX[2], 690, { align: "center" });
  text(ctx, "Sarcopenia", colX[3], 690, { align: "center" });

  rows.forEach((row, i) => {
    const y = 727 + i * 36;
    text(ctx, row.label, colX[0], y, { align: "center" });
    text(ctx, row.cutoff == null ? "-" : String(row.cutoff), colX[1], y, { align: "center", color: COLOR.text });
    text(ctx, fmt(row.value, 2 - (row.value >= 10 ? 1 : 0)), colX[2], y, { align: "center", color: COLOR.text });
    text(ctx, row.result, colX[3], y, { align: "center", color: COLOR.text });
  });

  return canvas;
}
*/

/* ------------------------------------------------------------------ */
/* PDF                                                               */
/* ------------------------------------------------------------------ */

function dataUrlToBytes(dataUrl) {
  const bin = atob(dataUrl.split(",")[1]);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

/** JPEG 1장을 전체 페이지로 채운 최소 구성 PDF (A4 세로 595.28x841.89pt) */
// export function jpegToPdfBlob(jpegBytes, imgW, imgH, pageW = 842, pageH = 595) {   // 이전 A4 가로
export function jpegToPdfBlob(jpegBytes, imgW, imgH, pageW = PDF_W, pageH = PDF_H) {
  const chunks = [];
  const offsets = [];
  let offset = 0;

  const push = (chunk) => {
    let bytes = chunk;
    if (typeof chunk === "string") {
      bytes = new Uint8Array(chunk.length);
      for (let i = 0; i < chunk.length; i++) bytes[i] = chunk.charCodeAt(i) & 0xff;
    }
    chunks.push(bytes);
    offset += bytes.length;
  };
  const obj = (n, write) => {
    offsets[n] = offset;
    push(`${n} 0 obj\n`);
    write();
    push("\nendobj\n");
  };

  const content = `q\n${pageW} 0 0 ${pageH} 0 0 cm\n/Im0 Do\nQ\n`;

  push("%PDF-1.4\n%âãÏÓ\n");
  obj(1, () => push("<< /Type /Catalog /Pages 2 0 R >>"));
  obj(2, () => push("<< /Type /Pages /Kids [3 0 R] /Count 1 >>"));
  obj(3, () =>
    push(
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageW} ${pageH}] ` +
        `/Resources << /XObject << /Im0 4 0 R >> >> /Contents 5 0 R >>`
    )
  );
  obj(4, () => {
    push(
      `<< /Type /XObject /Subtype /Image /Width ${imgW} /Height ${imgH} ` +
        `/ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${jpegBytes.length} >>\nstream\n`
    );
    push(jpegBytes);
    push("\nendstream");
  });
  obj(5, () => push(`<< /Length ${content.length} >>\nstream\n${content}endstream`));

  const xrefOffset = offset;
  let xref = "xref\n0 6\n0000000000 65535 f \n";
  for (let n = 1; n <= 5; n++) {
    xref += `${String(offsets[n]).padStart(10, "0")} 00000 n \n`;
  }
  push(`${xref}trailer\n<< /Size 6 /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF\n`);

  return new Blob(chunks, { type: "application/pdf" });
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * 리포트 PDF 생성 + 다운로드
 * @param store   sarcopeniaMeasureStore
 * @param patient { name, age, sex: "M"|"F", heightCm, weightKg }
 */
export function generateSarcopeniaReportPdf(store, patient) {
  const source = collectReportSource(store);
  const canvas = renderReportCanvas(source, patient);
  const jpeg = dataUrlToBytes(canvas.toDataURL("image/jpeg", 0.92));
  const blob = jpegToPdfBlob(jpeg, canvas.width, canvas.height);

  const slice = source.sliceNo != null && source.sliceNo !== "" ? `_slice${source.sliceNo}` : "";
  downloadBlob(blob, `Body-inside_Sarcopenia_Report${slice}.pdf`);
}
